import { request } from '../client';
import { endpoints } from '../endpoints';
import { getProject, setProjectLinks } from './projectsService';

/**
 * The current backend returns 200 + empty body when the project/target group
 * does not exist. Treat that as a failure instead of silently continuing.
 */
const requireBody = (res, what) => {
  if (res === null || res === undefined) throw new Error(`${what} was not saved: the server did not find the campaign or target group.`);
  return res;
};

export const createTargetGroup = async (projectId, payload = {}) =>
  requireBody(await request(endpoints.targetGroups.create(projectId), { method: 'POST', body: payload }), 'Target group');

/**
 * The response is the target group plus a `warnings` list (e.g. completes below the recommended 200). The warnings are
 * kept out of the target group so they are never stored with it; use saveBasicSettingsWithWarnings to read them.
 */
/**
 * Rename a target group and/or set its own survey URL (PATCH .../targetGroup/{tgid}). `surveyUrl: null` goes back to
 * the campaign's survey URL; a key that is left out is not changed. A URL that is not http(s) is a 400 with `errors`.
 * @param {string} projectId
 * @param {string} tgId
 * @param {{ name?: string, surveyUrl?: string | null }} patch
 */
export const updateTargetGroup = async (projectId, tgId, patch) =>
  requireBody(await request(endpoints.targetGroups.update(projectId, tgId), { method: 'PATCH', body: patch }), 'Target group');

export const saveBasicSettingsWithWarnings = async (projectId, tgId, payload) => {
  const res = requireBody(await request(endpoints.targetGroups.basicSettings(projectId, tgId), { method: 'POST', body: payload }), 'Basic settings');
  const { warnings, ...targetGroup } = res;
  return { targetGroup, warnings: Array.isArray(warnings) ? warnings.map(String) : [] };
};

export const saveBasicSettings = async (projectId, tgId, payload) => (await saveBasicSettingsWithWarnings(projectId, tgId, payload)).targetGroup;

export const saveQuotaSettings = async (projectId, tgId, payload) =>
  requireBody(await request(endpoints.targetGroups.quota(projectId, tgId), { method: 'POST', body: payload }), 'Quotas');

export const saveRegionSettings = async (projectId, tgId, payload) =>
  requireBody(await request(endpoints.targetGroups.region(projectId, tgId), { method: 'POST', body: payload }), 'Regions');

/**
 * Per the backend handover, POST .../targetGroup/create already adds the new group to the project's
 * `targetGroups`. This verifies that with a fresh read and only PATCHes if the link is missing, so it is
 * correct whether or not the backend links it (and never overwrites with a stale list).
 */
export async function ensureTargetGroupLinked(projectId, targetGroupId) {
  const project = await getProject(projectId);
  if (!project) throw new Error('The campaign could not be found.');
  const linked = (project.targetGroups || []).some((t) => (t && t.id) === targetGroupId);
  if (linked) return project;
  const ids = (project.targetGroups || []).map((t) => (t && t.id) || t);
  await setProjectLinks(project, { targetGroupIds: [...ids, targetGroupId] });
  return project;
}

/**
 * Replace the target group's excluded projects (campaigns whose respondents are kept out of this group).
 * Body is a plain array of project ids: it REPLACES the list, [] clears it. A target group cannot exclude its own campaign and an
 * unknown id is a 400 with `errors`. Stored only for now: it takes effect once respondent selection exists.
 * Resolves the target group with excludedProjects: [{ id, name, projectId, projectStatus }].
 */
export const saveExcludedProjects = async (projectId, tgId, projectIds) =>
  requireBody(await request(endpoints.targetGroups.excluded(projectId, tgId), { method: 'POST', body: [...new Set(projectIds)] }), 'Exclusions');

/**
 * Replace the target group's profiling selection. Body is a plain JSON array of profiling ANSWER ids
 * (backend handover / OpenAPI): it replaces the current selection, [] clears it, duplicates are ignored and an
 * unknown id is a 400 with nothing saved. The returned group carries profilingAnswers: [{ id, profiling_answers }].
 */
export const saveProfilingSelection = async (projectId, tgId, answerIds) =>
  requireBody(await request(endpoints.targetGroups.profiling(projectId, tgId), { method: 'POST', body: [...new Set(answerIds)] }), 'Profiling');
